<template>
<div class="bookings-section">

  <TopBar>
    <template v-slot:left>
      <button class="btn-report-special" @click="createReport" :disabled="isComponentBusy">
        <span v-if="!isCreatingReport">
          <i class="fa fa-download"></i> Export report
        </span>
        <span v-if="isCreatingReport">
          <i class="fa fa-spinner fa-spin"></i> Please wait
        </span>
      </button>
    </template>
    <template v-slot:right>
      <MonthSelector />
    </template>
  </TopBar>

  <div class="card">
    <div class="card-header">
      <i class="fa fa-spinner fa-spin card-header--icon" v-if="waitForListFill"></i>
      <i class="fa fa-book card-header--icon" v-else></i>
      <span class="card-header--title">Bookings Overview</span>
    </div>

    <div class="card-content">

      <PaginatedTableComponent
        :maxPage="maxPage"
        :sub-title="shortdate(dater.activeDate)"
        :data="bookings.data"
        :columns="bookings.columns"
        :headings="bookings.headings"
        :currentPage="currentPage"
        :isLoading="waitForListFill"
        @changePage="changePage"
      >
        <template #cell-invoices="{ item }">
          <div class="invoice-cell">
            <template v-if="!item.bookingAttributionId">
              <div class="invoice-empty">—</div>
            </template>
            <template v-else>
              <template v-if="!invoiceData[item.bookingAttributionId]">
                <i v-if="invoiceLoadingStates[item.bookingAttributionId]" class="invoice-loading-dot"></i>
                <div v-else class="invoice-empty">—</div>
              </template>
              <template v-else-if="invoiceData[item.bookingAttributionId].length === 0">
                <div class="invoice-empty">—</div>
              </template>
              <template v-else-if="invoiceData[item.bookingAttributionId].length === 1">
                <a
                  :href="invoiceData[item.bookingAttributionId][0].url"
                  target="_blank"
                  rel="noopener"
                  class="btn-invoice"
                  :title="invoiceData[item.bookingAttributionId][0].filename"
                >
                  <i class="fa fa-file-pdf-o"></i>
                </a>
              </template>
              <div v-else class="invoice-dropdown-wrapper">
                <button
                  class="btn-invoice"
                  @click="toggleDropdown(item.bookingAttributionId)"
                >
                  <i class="fa fa-file-pdf-o"></i>
                  <span class="invoice-badge">
                    {{ invoiceData[item.bookingAttributionId].length }}
                  </span>
                </button>
                <div v-if="openDropdown === item.bookingAttributionId" class="invoice-dropdown">
                  <a
                    v-for="(invoice, idx) in invoiceData[item.bookingAttributionId]"
                    :key="idx"
                    :href="invoice.url"
                    target="_blank"
                    rel="noopener"
                    class="invoice-dropdown-item"
                  >
                    <i class="fa fa-file-pdf-o"></i>
                    {{ invoice.filename }}
                  </a>
                </div>
              </div>
            </template>
          </div>
        </template>
      </PaginatedTableComponent>

    </div>
  </div>
</div>
</template>

<script>
import dayjs from 'dayjs';
import { shortdate } from '../filters'
import Dater from '@/repositories/dater'
import MonthSelector from '@/components/shared/MonthSelector.vue'
import PaginatedTableComponent from '@/components/shared/PaginatedTableComponent.vue'
import Api from '@/services/ApiGateway'
import { mapGetters, mapState } from 'vuex'
import TopBar from '../components/shared/TopBar.vue';

const filterBookings = (bookings) => {
  return bookings.map(booking => ({
    ...booking,
    bookingPrice: Number(booking.bookingPrice),
    bookedDate:  dayjs(booking.createdAt).format('DD/MM/YYYY'),
  }));
};

export default {
  components: {
    PaginatedTableComponent,
    MonthSelector,
    TopBar,
  },
  data() {
    return {
      dater: Dater,
      isCreatingReport: false,
      bookingsList: [],
      paginationSize: window.ferryhopper && window.ferryhopper.pagination_size || 50,
      tableStructure: [],
      hasAccessRightsToBookings: false,
      invoiceData: {},
      invoiceLoadingStates: {},
      openDropdown: null,
    }
  },
  watch: {
    // Executes instantly once component comes into view, and each time the date changes
    getActiveDates(previous, current) {
      this.$store.dispatch('bookingPagination/resetBookingsPageAction')
      this.invoiceData = {}
      this.invoiceLoadingStates = {}
      this.getBookingsList(1);
    }
  },
  computed: {
    ...mapState({
      currentPage: state => state.bookingPagination.currentBookingListsPage,
      waitForListFill: state => state.bookingPagination.bookingLockList,
      transactionsCount: state => state.statisticsModule.transactionsCount
    }),
    ...mapGetters({
      getActiveDates: 'datesModule/getActiveDates',
      impersonateUser: 'authentication/impersonateUser',
    }),
    bookings() {
      const headings = [...this.tableStructure.map(c => c.columnName)];
      const columns = [...this.tableStructure.map(c => c.itemProperty)];
      if (this.hasAccessRightsToBookings) {
        headings.push('Invoices');
        columns.push('invoices');
      }
      return {
        headings,
        columns,
        data: [...this.bookingsList],
      }
    },
    isComponentBusy() {
      return this.isCreatingReport || this.waitForListFill
    },
    maxPage() {
      return Math.ceil(this.transactionsCount / this.paginationSize)
    }
  },
  methods: {
    shortdate,
    createReport() {
      this.isCreatingReport = true
      Api.userReport('bookings', this.impersonateUser).then(res => {
        window.open(res.data.url, 'name')
      }).finally(() => this.isCreatingReport = false);
    },
    changePage(page) {
      this.$store.dispatch('bookingPagination/changeBookingsPageAction', page)
      this.invoiceData = {}
      this.invoiceLoadingStates = {}
      this.getBookingsList(page);
    },
    getBookingsList(page) {
      this.$store.dispatch('bookingPagination/lockBookingList')
      Api.fetchBookingsTablePage(page, this.impersonateUser).then(result => {
        const { model, bookings, bookingsCount, tableStructure, hasAccessRightsToBookings } = result.data;
        this.tableStructure = tableStructure;
        this.hasAccessRightsToBookings = !!hasAccessRightsToBookings;
        this.$store.dispatch('statisticsModule/setModel', model);
        if (!!bookingsCount) {
          this.$store.dispatch('statisticsModule/setTransactionsCount', bookingsCount)
        }
        this.bookingsList = filterBookings(bookings);
        this.$store.dispatch('bookingPagination/unlockBookingList')
        if (this.hasAccessRightsToBookings) {
          this.fetchBulkInvoices()
        }
      }).catch(error => console.log(error))
    },
    fetchBulkInvoices() {
      const ids = this.bookingsList
        .map(b => b.bookingAttributionId)
        .filter(id => !!id)
      if (ids.length === 0) return

      const loading = {}
      ids.forEach(id => { loading[id] = true })
      this.invoiceLoadingStates = { ...this.invoiceLoadingStates, ...loading }

      Api.fetchBookingInvoiceUrls(ids, this.impersonateUser)
        .then(response => {
          const map = response.data.invoices || {}
          const newData = {}
          ids.forEach(id => { newData[id] = map[id] || [] })
          this.invoiceData = { ...this.invoiceData, ...newData }
        })
        .catch(() => {
          const emptyData = {}
          ids.forEach(id => { emptyData[id] = [] })
          this.invoiceData = { ...this.invoiceData, ...emptyData }
        })
        .finally(() => {
          const done = {}
          ids.forEach(id => { done[id] = false })
          this.invoiceLoadingStates = { ...this.invoiceLoadingStates, ...done }
        })
    },
    toggleDropdown(id) {
      this.openDropdown = this.openDropdown === id ? null : id
    },
    closeDropdown(e) {
      if (!e.target.closest('.invoice-dropdown-wrapper')) {
        this.openDropdown = null
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.closeDropdown)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeDropdown)
  },
}
</script>

<style scoped>
.bookings-section {
  padding: 1rem 0 2rem 0;
}

.invoice-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.btn-invoice {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  border-radius: 4px;
  padding: 0;
  cursor: pointer;
  color: #555;
  font-size: 16px;
  transition: color 0.2s, background 0.2s;
}

.btn-invoice:hover {
  color: #0066cc;
  background: #e8f0fe;
}

.invoice-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  background: #0066cc;
  color: #fff;
  font-size: 9px;
  font-weight: 600;
  min-width: 12px;
  height: 12px;
  line-height: 12px;
  text-align: center;
  border-radius: 6px;
  padding: 0 2px;
}

.invoice-dropdown-wrapper {
  position: relative;
}

.invoice-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 100;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 180px;
  padding: 4px 0;
}

.invoice-dropdown-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  color: #333;
  text-decoration: none;
  font-size: 13px;
  white-space: nowrap;
}

.invoice-dropdown-item:hover {
  background: #e8f0fe;
  color: #0066cc;
}

.invoice-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: #999;
}

.invoice-loading-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #aaa;
  animation: invoice-pulse 1s ease-in-out infinite;
}

@keyframes invoice-pulse {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 1; }
}
</style>
